import { QueryClient } from "@tanstack/react-query";

const pendingTimers = new Map<string, number[]>();

/**
 * The worker translates ASYNCHRONOUSLY (Service Bus → Azure Translator → API callback), so a
 * single invalidation right after a save refetches BEFORE the translated cells exist — the user
 * then had to reload the page to see the translation appear. Re-invalidate on a staggered
 * schedule covering the worker's usual latency instead: mounted queries (values + flags) refetch
 * and the fresh cells surface live; unmounted ones are marked stale and refetch on next mount
 * (navigate-away-and-back also shows fresh data).
 *
 * Omit entityId to refresh every entity of the type (e.g. all experiences after a floating save).
 * A new schedule for the same scope replaces the previous one.
 */
export const scheduleTranslationRefresh = (
	queryClient: QueryClient,
	entityType: string,
	entityId?: number,
	delays: number[] = [4000, 10000, 20000, 35000]
): void => {
	const scope: (string | number)[] =
		entityId !== undefined ? [entityType, entityId] : [entityType];

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["entityTranslations", ...scope] });
		queryClient.invalidateQueries({ queryKey: ["entityTranslationFlags", ...scope] });
	};

	const key = scope.join(":");
	pendingTimers.get(key)?.forEach(timer => window.clearTimeout(timer));

	invalidate();
	pendingTimers.set(
		key,
		delays.map(delay => window.setTimeout(invalidate, delay))
	);
};
